import { __stringify, exists } from '../utils/tools.js';
import { tsValidateExists, tsValidateResult } from './validator-core.js';
import { currentTimeMillis } from '../utils/date-time-tools.js';
import { asArray, filterInstances } from '../utils/array-tools.js';
import { _keys } from '../utils/object-tools.js';
import { BadImplementationException } from '../core/exceptions/exceptions.js';
/**
 * Validates a dynamic object where both keys and values are validated.
 *
 * Validates each key-value pair in the object using separate validators.
 * Returns an object with validation errors keyed by the object's keys.
 *
 * **Error aggregation**: If both key and value fail validation, combines both errors.
 * If only one fails, reports that specific error.
 *
 * @template T - Object type to validate
 * @param valuesValidator - Validator for object values
 * @param keysValidator - Validator for object keys
 * @param mandatory - Whether the object itself is required (default: true)
 * @returns Validator that validates both keys and values
 */
export const tsValidateDynamicObject = (valuesValidator, keysValidator, mandatory = true) => {
    return [tsValidateExists(mandatory),
        (input) => {
            if (!input)
                return;
            const keys = _keys(input);
            const _result = keys.reduce((res, key) => {
                const _valRes = tsValidateResult(input[key], valuesValidator);
                const _keyRes = tsValidateResult(key, keysValidator);
                if (_valRes && _keyRes)
                    res[key] = `Key: ${_keyRes}\nValue: ${_valRes}`;
                else if (_valRes)
                    res[key] = 'Value: ' + __stringify(_valRes, true);
                else if (_keyRes)
                    res[key] = 'Key: ' + __stringify(_keyRes, true);
                return res;
            }, {});
            return _keys(_result).length ? _result : undefined;
        }];
};
/**
 * Validates input against multiple validators (union type).
 *
 * The input must pass at least one of the provided validators. If all validators
 * fail, returns an array with an error message and all validation results.
 *
 * **Early exit**: Returns undefined (valid) as soon as one validator passes.
 *
 * @template T - Type to validate
 * @param validators - Array of validators to try
 * @param mandatory - Whether input is required (default: true)
 * @returns Validator that tries each validator until one passes
 */
export const tsValidateUnion = (validators, mandatory = true) => {
    return [tsValidateExists(mandatory),
        (input) => {
            const results = [];
            for (const validator of validators) {
                const _res = tsValidateResult(input, validator);
                if (!_res)
                    return;
                results.push(_res);
            }
            return filterInstances(results).length !== 0 ? ['Input does not match any of the possible types',
                results] : undefined;
        }];
};
export const tsValidateCustom = (processor, mandatory = true) => {
    return [tsValidateExists(mandatory), processor];
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const typeFunc = (type) => typeof type;
export const tsValidateUnionV3 = (validatorObject, mandatory = true) => {
    return [tsValidateExists(mandatory),
        (input) => {
            const _type = typeof input;
            return _keys(validatorObject)
                .includes(_type) ? tsValidateResult(input, validatorObject[_type]) : 'Input does not match any of the possible types';
        }];
};
/**
 * Validates an array by validating each element.
 *
 * Applies the validator to each element in the array. Returns an array of
 * validation results (one per element) if any elements fail. Supports multiple
 * validators (all must pass for each element) and minimum length checking.
 *
 * **Strict mode**: When `strict=true`, validation errors include element indices.
 * When `strict=false`, errors may be less specific.
 *
 * @template T - Array type
 * @template I - Element type
 * @param validator - Validator(s) to apply to each element (can be array for multiple validators)
 * @param mandatory - Whether array is required (default: true)
 * @param minimumLength - Minimum array length (default: 0)
 * @param strict - Whether to use strict validation mode (default: true)
 * @returns Validator that validates each array element
 */
export const tsValidateArray = (validator, mandatory = true, minimumLength = 0, strict = true) => {
    return [tsValidateExists(mandatory),
        ...asArray(validator).map(validator => {
            return (input) => {
                const results = [];
                const _input = input;
                if (_input.length < minimumLength)
                    return 'Array length smaller than minimum defined length';
                for (let i = 0; i < _input.length; i++) {
                    results[i] = tsValidateResult(_input[i], validator, undefined, strict, input);
                }
                return filterInstances(results).length !== 0 ? results : undefined;
            };
        })];
};
/**
 * Validates a string with optional length constraints.
 *
 * Supports:
 * - Single maximum length: `length = 10` (max 10 chars)
 * - Range: `length = [5, 10]` (min 5, max 10 chars)
 * - No limit: `length = -1` (any length)
 *
 * @param length - Maximum length, or [min, max] range, or -1 for no limit (default: -1)
 * @param mandatory - Whether string is required (default: true)
 * @returns Validator for strings
 */
export const tsValidateString = (length = -1, mandatory = true) => {
    return [tsValidateExists(mandatory),
        (input) => {
            // noinspection SuspiciousTypeOfGuard
            if (typeof input !== 'string')
                return `input is not a string`;
            if (Array.isArray(length)) {
                if (length[0] !== -1 && length[0] > input.length)
                    return `input length is lesser than ${length[0]}`;
                if (length[1] !== -1 && input.length > length[1])
                    return `input length is longer than ${length[1]}`;
            }
            else if (length !== -1 && input.length > length)
                return `input length is longer than ${length}`;
            return;
        }];
};
export const tsValidateAnyString = tsValidateString();
export const tsValidateOptionalAnyString = tsValidateString(-1, false);
export const tsValidateStringMinLength = (length, mandatory = true) => {
    return [tsValidateExists(mandatory),
        (input) => {
            // noinspection SuspiciousTypeOfGuard
            if (typeof input !== 'string')
                return `input is not a string`;
            if (input.length >= length)
                return;
            return `input has less than ${length} chars`;
        }];
};
export const tsValidateNumber = (mandatory = true) => {
    return [tsValidateExists(mandatory),
        (input) => {
            // noinspection SuspiciousTypeOfGuard
            if (typeof input === 'number')
                return;
            return `Input is not a number! \nvalue: ${input}\ntype: ${typeof input}`;
        }];
};
export const tsValidateAnyNumber = tsValidateNumber();
export const tsValidateOptionalAnyNumber = tsValidateNumber(false);
export const tsValidateEnum = (enumType, mandatory = true) => {
    return [tsValidateExists(mandatory),
        (input) => {
            // @ts-ignore
            if (exists(enumType[input]))
                return;
            return `Input is not a valid enum value in ${__stringify(enumType)}`;
        }];
};
export const tsValidateBoolean = (mandatory = true) => {
    return [tsValidateExists(mandatory),
        (input) => {
            // noinspection SuspiciousTypeOfGuard
            if (typeof input === 'boolean')
                return;
            return `Input is not a boolean! \nvalue: ${input}\ntype: ${typeof input}`;
        }];
};
export const tsValidateValue = (values, mandatory = true) => {
    return [tsValidateExists(mandatory),
        (input) => {
            if (values.includes(input))
                return;
            return `Input is not valid:\n  input: ${input && __stringify(input) || input}\n  options: ${__stringify(values)}\n`;
        }];
};
export const tsValidateIsInRange = (ranges, mandatory = true) => {
    return [tsValidateExists(mandatory),
        (input) => {
            if (ranges.some((range) => range[0] <= input && input <= range[1]))
                return;
            return `Input is not valid: ${input}.  It should fit one of the expected range: ${__stringify(ranges)}`;
        }];
};
export const tsValidateRange = (mandatory = true) => {
    return [tsValidateExists(mandatory),
        (input) => {
            if (!input)
                return 'Missing range';
            if (typeof input[0] !== 'number' || typeof input[1] !== 'number')
                return `Got range value that's not a number`;
            if (input[0] > input[1]) {
                return 'Range min is larger than range max';
            }
            return;
        }];
};
export const tsValidateRegexp = (regexp, mandatory = true) => {
    return [tsValidateExists(mandatory),
        (input) => {
            // console.log({input, regexp});
            if (regexp.test(input))
                return;
            return `Input does not match regexp:\n  input: ${input}\n  regexp: ${regexp}\n`;
        }];
};
/**
 * Validates a timestamp is within a specified time interval from now.
 *
 * Checks that the timestamp is between `(now - interval)` and `now`.
 * If no interval is provided, defaults to checking if timestamp is between 0 and now
 * (essentially any past or present timestamp).
 *
 * @param interval - Maximum age in milliseconds (default: current time, i.e., any past timestamp)
 * @param mandatory - Whether timestamp is required (default: true)
 * @returns Validator for timestamps
 */
export const tsValidateTimestamp = (interval, mandatory = true) => {
    return [tsValidateExists(mandatory),
        (input) => {
            const now = currentTimeMillis();
            const minTimestamp = now - (interval || now);
            if (minTimestamp <= input && input <= now)
                return;
            return `Input is not valid:\n  input: ${input}\n  Expected Interval: ${minTimestamp} - ${now}\n`;
        }];
};
export const tsValidateAudit = (range = { min: 0, max: Number.MAX_VALUE }) => {
    return (audit) => {
        return tsValidateResult(audit?.auditAt?.timestamp, tsValidateIsInRange([[range.min, range.max]]));
    };
};
export const tsValidateTimeRange = (mandatory = true) => {
    return [tsValidateExists(mandatory), (instance) => {
            if (!instance)
                return 'No instance was provided to validation';
            if (!instance.length || instance.every(value => value === undefined))
                return 'Empty time range provided';
            if (instance.length > 2)
                return 'Time range provided has too many values';
            if (!instance[0] && typeof instance[1] === 'number')
                return;
            if (!instance[1] && typeof instance[0] === 'number')
                return;
            return tsValidateResult(instance, tsValidateRange());
        }];
};
export const tsValidateNonMandatoryObject = (validator) => {
    return [tsValidateExists(false),
        (input) => tsValidateResult(input, validator)];
};
export const tsValidateOptionalObject = tsValidateNonMandatoryObject;
export const tsValidator_valueByKey = (validatorObject, prop = 'type') => {
    return tsValidateCustom((value, parentObject) => {
        return tsValidateResult(value, validatorObject[parentObject[prop]]);
    });
};
/**
 * Validates an array of objects using different validators based on a key value.
 *
 * For each object in the array, looks up a validator from `validatorMap` using
 * the value of the specified `key`. This enables polymorphic validation where
 * different object types in the array are validated differently.
 *
 * **Example**: Array of shapes where each shape has a `type` field ('circle' or 'square'),
 * and different validators are used based on the type.
 *
 * @template T - Object type
 * @param key - Key to use for validator lookup
 * @param validatorMap - Map of key values to validators
 * @returns Validator for arrays of objects
 * @throws BadImplementationException if a key value has no corresponding validator
 */
export const tsValidator_ArrayOfObjectsByKey = (key, validatorMap) => {
    return tsValidateArray(tsValidateCustom((value) => {
        const _value = value;
        const validator = validatorMap[_value[key]];
        if (!validator)
            throw new BadImplementationException(`No validator defined for key ${key} with value ${_value[key]}`);
        return tsValidateResult(_value, validator);
    }));
};
export const tsValidator_stringOrNumber = (mandatory = true) => {
    return tsValidateCustom((input) => {
        switch (typeof input) {
            case 'string':
                return tsValidateResult(input, tsValidateString());
            case 'number':
                return tsValidateResult(input, tsValidateNumber());
        }
        return 'Input is not string or number.';
    }, mandatory);
};
export const tsValidateIpAddress = (mandatory = true) => {
    return [tsValidateExists(mandatory), (input) => {
            if (!input && mandatory)
                return 'no input provided for validation of mandatory prop';
            if (tsValidateResult(input, tsValidateRegexp(/(([0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])/)))
                return;
            if (tsValidateResult(input, tsValidateRegexp(/((([0-9a-fA-F]){1,4})\:){7}([0-9a-fA-F]){1,4}/)))
                return;
            return `Invalid input ${input}, not an ip address`;
        }];
};
//# sourceMappingURL=type-validators.js.map